import React from "react";
import { FaCheck, FaPen, FaPlus } from "react-icons/fa";
import { GiGong } from "react-icons/gi";

import {
  getFullName,
  getClubName,
  getId,
  getWinnerIdFromCombat,
  getResultForCompetitor,
  isRealCompetitor,
  safeNumber,
  normalizeResult,
} from "./systematicRepechageUtils";

const resultChipClasses = (result) => {
  switch (normalizeResult(result)) {
    case "V":
      return "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-200 dark:ring-emerald-400/30";
    case "D":
      return "bg-rose-100 text-rose-700 ring-1 ring-rose-200 dark:bg-rose-500/15 dark:text-rose-200 dark:ring-rose-400/30";
    case "N":
      return "bg-amber-100 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-200 dark:ring-amber-400/30";
    case "F":
      return "bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-500/15 dark:text-slate-200 dark:ring-slate-400/30";
    case "H":
      return "bg-red-100 text-red-800 ring-1 ring-red-200 dark:bg-red-500/20 dark:text-red-200 dark:ring-red-400/40";
    default:
      return "bg-slate-100 text-slate-500 ring-1 ring-slate-200 dark:bg-white/10 dark:text-slate-300 dark:ring-white/10";
  }
};

const rowClasses = (role) => {
  switch (role) {
    case "winner":
      return {
        box: "border-emerald-300 bg-emerald-50 text-emerald-950 dark:border-emerald-400/40 dark:bg-emerald-500/15 dark:text-emerald-50",
        badge: "bg-emerald-500 text-white",
        club: "text-emerald-700/80 dark:text-emerald-100/75",
      };

    case "loser":
      return {
        box: "border-slate-200 bg-slate-50 text-slate-600 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-300",
        badge: "bg-slate-300 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
        club: "text-slate-400 dark:text-slate-500",
      };

    case "pending":
      return {
        box: "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-100",
        badge: "bg-amber-400 text-white",
        club: "text-amber-700/70 dark:text-amber-100/60",
      };

    case "bye":
      return {
        box: "border-dashed border-slate-200 bg-slate-50/80 text-slate-400 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-500",
        badge: "bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-500",
        club: "text-slate-400 dark:text-slate-600",
      };

    default:
      return {
        box: "border-slate-200 bg-white text-slate-900 dark:border-white/10 dark:bg-slate-900 dark:text-white",
        badge: "bg-slate-900 text-white dark:bg-white dark:text-slate-900",
        club: "text-slate-500 dark:text-slate-400",
      };
  }
};

const getParticipantRole = ({ competitor, combat }) => {
  if (!competitor) return "bye";
  if (competitor.pending) return "pending";

  const winnerId = getWinnerIdFromCombat(combat);
  const competitorId = getId(competitor);

  if (combat && winnerId) {
    return competitorId === winnerId ? "winner" : "loser";
  }

  return "default";
};

const ParticipantRow = ({ competitor, combat, position }) => {
  const role = getParticipantRole({
    competitor,
    combat,
  });

  const classes = rowClasses(role);
  const result = getResultForCompetitor(combat, competitor);
  const hasResult = combat && isRealCompetitor(competitor);

  return (
    <div
      className={`flex min-h-[48px] items-center gap-3 rounded-2xl border px-3 py-2 ${classes.box}`}
    >
      <div
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-xl text-[11px] font-bold ${classes.badge}`}
      >
        {role === "winner" ? (
          <FaCheck className="text-[11px]" />
        ) : role === "pending" ? (
          "…"
        ) : role === "bye" ? (
          "-"
        ) : (
          position
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold leading-tight">
          {competitor?.pending
            ? competitor.label || "En attente"
            : competitor
            ? getFullName(competitor)
            : "Aucun adversaire"}
        </p>

        <p className={`mt-0.5 truncate text-[11px] font-medium ${classes.club}`}>
          {competitor?.pending
            ? "Combat principal ou repêchage à saisir"
            : competitor
            ? getClubName(competitor) || "\u00A0"
            : "Qualification automatique"}
        </p>
      </div>

      {hasResult && (
        <div
          className={`shrink-0 rounded-full px-2 py-1 text-[11px] font-bold ${resultChipClasses(
            result.result,
          )}`}
        >
          {result.result || "-"}
          {result.points !== null && result.points !== undefined
            ? ` · ${safeNumber(result.points)}`
            : ""}
        </div>
      )}
    </div>
  );
};

const ScoreSummary = ({ combat, c1, c2 }) => {
  if (!combat || !isRealCompetitor(c1) || !isRealCompetitor(c2)) {
    return null;
  }

  const r1 = getResultForCompetitor(combat, c1);
  const r2 = getResultForCompetitor(combat, c2);

  const p1 =
    r1.points !== null && r1.points !== undefined
      ? safeNumber(r1.points)
      : "-";

  const p2 =
    r2.points !== null && r2.points !== undefined
      ? safeNumber(r2.points)
      : "-";

  return (
    <div className="flex min-w-0 flex-1 items-center justify-between rounded-2xl bg-slate-50 px-3 py-2 text-xs ring-1 ring-slate-200 dark:bg-white/[0.04] dark:ring-white/10">
      <span className="font-semibold text-slate-400 dark:text-slate-500">
        Score
      </span>

      <div className="flex items-center gap-2 font-bold text-slate-800 dark:text-slate-100">
        <span>{p1}</span>
        <span className="text-slate-300 dark:text-slate-600">/</span>
        <span>{p2}</span>
      </div>
    </div>
  );
};

const MatchAction = ({
  match,
  onOpenCreate,
  onOpenEdit,
  onOpenScoreboard,
}) => {
  const c1 = match.competiteur1;
  const c2 = match.competiteur2;

  const canPlay = isRealCompetitor(c1) && isRealCompetitor(c2);

  if (!canPlay) {
    if ((isRealCompetitor(c1) && !c2) || (!c1 && isRealCompetitor(c2))) {
      return (
        <div className="inline-flex items-center justify-center rounded-2xl bg-slate-50 px-3 py-2 text-xs font-bold text-slate-500 ring-1 ring-slate-200 dark:bg-white/[0.04] dark:text-slate-400 dark:ring-white/10">
          Exempt
        </div>
      );
    }

    return (
      <div className="inline-flex items-center justify-center rounded-2xl bg-amber-50 px-3 py-2 text-xs font-bold text-amber-700 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-100 dark:ring-amber-400/20">
        En attente
      </div>
    );
  }

  if (match.combat) {
    return (
      <button
        type="button"
        onClick={() =>
          onOpenEdit?.(
            match.combat,
            c1,
            c2,
            match.position1,
            match.position2,
          )
        }
        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-3 py-2 text-xs font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-blue-700"
      >
        <FaPen className="text-[11px]" />
        Modifier
      </button>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <button
        type="button"
        onClick={() =>
          onOpenScoreboard?.(
            c1,
            c2,
            match.position1,
            match.position2,
          )
        }
        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-3 py-2 text-xs font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
      >
        <GiGong className="text-sm" />
        Tableau score
      </button>

      <button
        type="button"
        onClick={() =>
          onOpenCreate?.(
            c1,
            c2,
            match.position1,
            match.position2,
          )
        }
        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-3 py-2 text-xs font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-emerald-700"
      >
        <FaPlus className="text-[11px]" />
        Saisir
      </button>
    </div>
  );
};

const getCardClasses = (hasWinner) => {
  if (hasWinner) {
    return "border-emerald-200 bg-white shadow-[0_14px_34px_rgba(16,185,129,0.10)] dark:border-emerald-400/25 dark:bg-slate-950";
  }

  return "border-slate-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-slate-950";
};

const SystematicRepechageMatchCard = ({
  match,
  cardHeight = 178,
  onOpenCreate,
  onOpenEdit,
  onOpenScoreboard,
}) => {
  const hasWinner = !!match.winnerId || !!match.autoWinner;

  return (
    <div
      className={`h-full rounded-[1.55rem] border p-3 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl ${getCardClasses(
        hasWinner,
      )}`}
      style={{
        minHeight: cardHeight,
      }}
    >
      <div className="flex h-full flex-col">
        <div className="space-y-2">
          <ParticipantRow
            competitor={match.competiteur1}
            combat={match.combat}
            position={1}
          />

          <ParticipantRow
            competitor={match.competiteur2}
            combat={match.combat}
            position={2}
          />
        </div>

        <div className="mt-auto flex items-center justify-between gap-2 pt-2">
          <ScoreSummary
            combat={match.combat}
            c1={match.competiteur1}
            c2={match.competiteur2}
          />

          <div className="ml-auto shrink-0">
            <MatchAction
              match={match}
              onOpenCreate={onOpenCreate}
              onOpenEdit={onOpenEdit}
              onOpenScoreboard={onOpenScoreboard}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystematicRepechageMatchCard;